import { useContext } from 'react'
import { useT } from './hooks'
import AuthContext from 'contexte/AuthContext'
import Tableau from 'components/DataComponents/Tableau/Tableau'
import TickerImage from 'components/DataComponents/TickerImage/TickerImage'
import DateSelector from 'components/Filters/DateSelector/DateSelector'
import BBookZoneSelect from './components/BBookZoneSelect'
import PageLoader from 'components/pageLoader/PageLoader'
import { fetchBBookPnlReport } from 'services/bBookServices'
import { bucketize, tickLabel } from './report/charts'
import { HousePnlChart, InstrumentPnlChart, FeesSourcesChart, money, signedMoney } from './report/pnlCharts'
import { useReport } from './report/useReport'
import BBookLive from './components/BBookLive'
import BBookLedger from './components/BBookLedger'
import BBookKpi from './components/BBookKpi'
import BBookInfo from './components/BBookInfo'
import Badge from 'components/Badge/Badge'
import BBookEmpty, { BBookLoadError } from './components/BBookEmpty'
import './bBook.css'

const DAILY_FIELDS = ['house_pnl', 'pairing_slippage', 'pairing_result', 'commissions', 'spread', 'overnight']
const SIDE_BADGE = { LONG: '#0F766E', SHORT: '#C2410C' } // darker than the shared badge colours, so white text reaches 4.5:1
const extreme = (rows, key, pick) => rows.reduce((best, row) => (best === null || pick(row[key], best[key]) ? row : best), null)

// Same content as the former Grafana "house P&L and risk" dashboard: live risk on top, the chosen period below.
const BBookPnl = () => {
  const { t } = useT()
  const { user } = useContext(AuthContext)
  const { selectedDate, setSelectedDate, zone, setZone, report, loading, failed, retry } = useReport(fetchBBookPnlReport, user, 0) // today by default

  const empty = t('pages.b_book_pnl.empty')
  const { rows: periods, unit } = report ? bucketize(report.daily, report.range, DAILY_FIELDS) : { rows: [], unit: 'day' }
  const period = t(`pages.b_book_report.period.${unit}`)
  const periodLabels = { day: t('pages.b_book_report.period_prefix.day'), week: t('pages.b_book_report.period_prefix.week'), month: t('pages.b_book_report.period_prefix.month') }
  const tick = tickLabel(unit)
  const section = (key) => (
    <div className='b_book_report_section'>
      <h2 id={`b-book-pnl-${key}`}>{t(`pages.b_book_pnl.${key}.title`)}</h2>
      <p className='b_book_hint'>{t(`pages.b_book_pnl.${key}.hint`)}</p>
    </div>
  )

  const directionLabel = (row) => {
    const label = t(`pages.b_book_pnl.pairings.directions.${row.direction || 'B_TO_A'}`)
    return ['failed', 'partial', 'sent'].includes(row.status) ? `${label} (${t(`pages.b_book_pnl.pairings.statuses.${row.status}`)})` : label
  }
  const reasonLabel = (reason) => (reason ? t(`pages.b_book_pnl.pairings.reasons.${reason}`, { defaultValue: reason }) : '—')
  const pairingRows = (report?.pairings || []).map((row) => ({
    data: [
      new Date(row.at).toLocaleString('fr-FR', { timeZone: zone, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
      <TickerImage ticker={row.symbol} />,
      directionLabel(row),
      <Badge text={row.account.toUpperCase()} color={SIDE_BADGE[row.account.toUpperCase()]} />,
      <span className='b_book_num'>{row.quantity}</span>,
      reasonLabel(row.reason),
      <span className='b_book_num'>{row.funded_margin_eur === null || row.funded_margin_eur === undefined ? '—' : row.funded_margin_eur > 0 ? money(row.funded_margin_eur) : '0'}</span>,
    ],
    utils: { id: `${row.at}-${row.symbol}-${row.account}` },
  }))

  const feeRows = (report?.fees.instruments || []).map((row) => ({
    data: [<TickerImage ticker={row.ticker} />, <span className='b_book_num'>{row.fees}</span>, <span className='b_book_num'>{money(row.commissions)}</span>,
      <span className='b_book_num'>{money(row.spread)}</span>, <strong className='b_book_num'>{money(row.total)}</strong>],
    utils: { id: row.ticker },
  }))

  // one sentence per chart for screen readers: the point of the chart, not a list of every bar
  const traded = periods.filter((row) => row.house_pnl || row.pairing_result)
  const best = extreme(traded, 'house_pnl', (a, b) => a > b)
  const worst = extreme(traded, 'house_pnl', (a, b) => a < b)
  const instruments = report?.house.instruments || []
  const summaries = report && {
    daily: best ? t('pages.b_book_pnl.summary.daily', { period, total: signedMoney(report.house.realized), best: `${tick(best.day)} (${signedMoney(best.house_pnl)})`, worst: `${tick(worst.day)} (${signedMoney(worst.house_pnl)})` }) : empty,
    instruments: instruments.length ? t('pages.b_book_pnl.summary.instruments', { worst: instruments[0].ticker, worstValue: signedMoney(instruments[0].house_pnl), best: instruments[instruments.length - 1].ticker, bestValue: signedMoney(instruments[instruments.length - 1].house_pnl) }) : empty,
    fees: t('pages.b_book_pnl.summary.fees', { total: money(report.fees.total), commissions: money(report.fees.commissions), spread: money(report.fees.spread), overnight: money(report.fees.overnight) }),
  }

  return (
    <>
        <div className='b_book_title_component'>
          <h1 className='b_book_page_title'>{t('pages.b_book_pnl.title')}</h1>
        </div>
        <BBookLive user={user} />

        <div className='b_book_report_section b_book_pnl_period_head'>
          <h2>{t('pages.b_book_pnl.period_title')}</h2>
          <div className='b_book_period_controls'>
            <BBookZoneSelect zone={zone} setZone={setZone} />
            <DateSelector selectedDate={selectedDate} setSelectedDate={setSelectedDate} withComparaison={false} />
          </div>
        </div>

        {failed && report && (
          <p className='b_book_error_text b_book_inline_error' role='alert'>
            {t('pages.b_book.stale_report')} <button className='b_book_link_button' onClick={retry}>{t('pages.b_book.retry')}</button>
          </p>
        )}

        {loading && !report ? (
          <div className='pageLoaderBox'><PageLoader /></div>
        ) : !report ? (
          failed && <BBookLoadError message={t('notifications.b_book.pnl.fetch_error')} retryLabel={t('pages.b_book.retry')} onRetry={retry} />
        ) : (
          <div className={loading ? 'b_book_report_refreshing' : ''} aria-busy={loading || undefined}>
            <section aria-labelledby='b-book-pnl-history'>
              {section('history')}
              <div className='b_book_kpis'>
                <BBookKpi label={t('pages.b_book_pnl.history.realized')} value={report.house.realized} format='signed' info={t('pages.b_book_info.history_realized')} />
                <BBookKpi label={t('pages.b_book_pnl.history.pairing_result')} value={report.house.pairing_result} format='signed'
                  info={t('pages.b_book_info.pairing_result')} hint={t('pages.b_book_pnl.history.pairing_result_hint')} />
                <BBookKpi label={t('pages.b_book_pnl.history.pairing_slippage')} value={report.house.pairing_slippage} format='signed'
                  info={t('pages.b_book_info.pairing_slippage')} hint={t('pages.b_book_pnl.history.pairing_slippage_hint')} />
              </div>
              <details className='b_book_details'>
                <summary>{t('pages.b_book_pnl.history.charts')}</summary>
              <div className='b_book_report_grid'>
                <div className='b_book_report_card b_book_report_card_wide'>
                  <BBookInfo label={t('pages.b_book_pnl.history.daily', { period })} text={t('pages.b_book_info.chart_daily_pnl')} />
                  <h3>{t('pages.b_book_pnl.history.daily', { period })}</h3>
                  <HousePnlChart data={periods} unit={unit} periodLabels={periodLabels} summary={summaries.daily} emptyLabel={empty}
                    labels={{ house_pnl: t('pages.b_book_pnl.history.house_pnl'), pairing_result: t('pages.b_book_pnl.history.pairing_result_short') }} />
                </div>
                <div className='b_book_report_card'>
                  <BBookInfo label={t('pages.b_book_pnl.history.instruments')} text={t('pages.b_book_info.chart_instruments_pnl')} />
                  <h3>{t('pages.b_book_pnl.history.instruments')}</h3>
                  <InstrumentPnlChart rows={instruments} label={t('pages.b_book_pnl.history.house_pnl')} summary={summaries.instruments} emptyLabel={empty} />
                </div>
              </div>
              </details>
            </section>

            <section aria-labelledby='b-book-pnl-ledger'>
              {section('ledger')}
              <BBookLedger ledger={report.ledger} zone={zone} />
            </section>

            <section aria-labelledby='b-book-pnl-pairings'>
              {section('pairings')}
              <div className='b_book_report_grid'>
                <div className='b_book_report_card b_book_report_card_full'>
                  <BBookInfo label={t('pages.b_book_pnl.pairings.title')} text={t('pages.b_book_info.pairings_table')} />
                  <h3>{t('pages.b_book_pnl.pairings.table_title', { count: pairingRows.length })}</h3>
                  {report.funded_margin?.total > 0 && <p className='b_book_hint'>{t('pages.b_book_pnl.pairings.funded_total', { amount: money(report.funded_margin.total) })}</p>}
                  {pairingRows.length ? (
                    <Tableau thead={['date', 'instrument', 'direction', 'side', 'quantity', 'reason', 'funded'].map((key) => t(`pages.b_book_pnl.pairings.table.${key}`))} tbody={pairingRows} />
                  ) : <BBookEmpty title={t('pages.b_book_pnl.pairings.empty')} hint={t('pages.b_book_pnl.pairings.empty_hint')} />}
                </div>
              </div>
            </section>

            <section aria-labelledby='b-book-pnl-revenue'>
              {section('revenue')}
              <div className='b_book_kpis b_book_kpis_4'>
                <BBookKpi label={t('pages.b_book_pnl.revenue.commissions')} value={report.fees.commissions} info={t('pages.b_book_info.commissions')} />
                <BBookKpi label={t('pages.b_book_pnl.revenue.spread')} value={report.fees.spread} info={t('pages.b_book_info.spread')} />
                <BBookKpi label={t('pages.b_book_pnl.revenue.overnight')} value={report.fees.overnight} info={t('pages.b_book_info.overnight')} />
                <BBookKpi label={t('pages.b_book_pnl.revenue.total')} value={report.fees.total} info={t('pages.b_book_info.fees_total')} />
              </div>
              <details className='b_book_details'>
                <summary>{t('pages.b_book_pnl.revenue.charts')}</summary>
                <div className='b_book_report_grid'>
                  <div className='b_book_report_card b_book_report_card_full'>
                    <BBookInfo label={t('pages.b_book_pnl.revenue.daily', { period })} text={t('pages.b_book_info.chart_fees')} />
                    <h3>{t('pages.b_book_pnl.revenue.daily', { period })}</h3>
                    <FeesSourcesChart data={periods} unit={unit} periodLabels={periodLabels} summary={summaries.fees} emptyLabel={empty}
                      labels={{ commissions: t('pages.b_book_pnl.revenue.commissions'), spread: t('pages.b_book_pnl.revenue.spread'), overnight: t('pages.b_book_pnl.revenue.overnight') }} />
                  </div>
                </div>
              </details>
              <div className='b_book_report_grid'>
                <div className='b_book_report_card b_book_report_card_full'>
                  <BBookInfo label={t('pages.b_book_pnl.revenue.instruments')} text={t('pages.b_book_info.fees_instruments')} />
                  <h3>{t('pages.b_book_pnl.revenue.instruments')}</h3>
                  <p className='b_book_hint'>{t('pages.b_book_pnl.revenue.instruments_hint')}</p>
                  {feeRows.length ? (
                    <Tableau thead={[t('pages.b_book_report.table.instrument'), t('pages.b_book_pnl.table.fees'), t('pages.b_book_report.table.commissions'), t('pages.b_book_report.table.spread'), t('pages.b_book_report.table.total')]} tbody={feeRows} />
                  ) : <BBookEmpty title={empty} />}
                </div>
              </div>
            </section>
          </div>
        )}
    </>
  )
}

export default BBookPnl
